import React, { useCallback, useEffect, useRef } from "react";
import PT from "prop-types";
import styled from "styled-components";
import { useSpring, animated as a, interpolate } from "react-spring";
import useWindowScroll from "@react-hook/window-scroll";
import { isBrowser } from "browser-or-node";
import { useWindowWidth } from "@react-hook/window-size";
import { useTranslation } from "gatsby-plugin-react-i18next";

import { down, grid_down, spacings } from "~/src/config/vars";
import { Container, Headline } from "~/src/components";
import { BlockVideoItem } from "~/src/components/blocks";
import useScrollWidth from "~/src/hook/useScrollWidth";

function ScrollCarousel({ children }) {
    const { t } = useTranslation();

    const refHeight = useRef(null);
    const refTransform = useRef(null);

    const { scrollWidth } = useScrollWidth(refTransform);

    const scrollY = useWindowScroll(45);
    const [{ st, xy }, set] = useSpring(() => ({ st: 0, xy: [0, 0] }));

    useEffect(() => {
        set({ st: scrollY });
    }, [scrollY, set]);

    const onMouseMove = useCallback(
        ({ clientX: x, clientY: y }) =>
            set({
                xy: [x - window.innerWidth / 2, y - window.innerHeight / 2],
            }),
        []
    );

    const top = refHeight.current ? refHeight.current.offsetTop : 0;
    const width = refHeight.current ? refHeight.current.offsetWidth : 0;

    const elHeight =
        scrollWidth - (window.innerWidth - window.innerHeight) + width / 10; // scroll away when final viewport width is 0.5 done

    const interpTransform = interpolate([st, xy], (o, xy) => {
        const mouseMoveDepth = 40;
        const x = width - (top - o) - width;

        if (x < -window.innerHeight - width * 0.5) {
            return `translate3d(${window.innerHeight}px, 0, 0)`;
        }

        if (Math.abs(x) > elHeight) {
            return `translate3d(${elHeight}px, 0, 0)`;
        }

        return `translate3d(${-x + -xy[0] / mouseMoveDepth}px, ${
            -xy[1] / mouseMoveDepth
        }px, 0)`;
    });

    return (
        <SScrollCarousel
            onMouseMove={onMouseMove}
            ref={refHeight}
            style={{ height: elHeight }}
        >
            <SStickyBox>
                <STitle>
                    <Container size="large">
                        <Headline
                            level={2}
                            size="huge"
                            offset={["top-40", "bottom-0"]}
                        >
                            {t("block_video_title")}
                        </Headline>
                    </Container>
                </STitle>
                <TransformBox
                    style={{ transform: interpTransform }}
                    ref={refTransform}
                >
                    {children}
                </TransformBox>
            </SStickyBox>
        </SScrollCarousel>
    );
}

ScrollCarousel.propTypes = {
    children: PT.any,
};

const SScrollCarousel = styled.div`
    position: relative;
`;

const STitle = styled.div`
    ${down("md")} {
        padding: 0 0 ${spacings?.offset_40};
    }
`;

const SStickyBox = styled.div`
    position: sticky;
    top: 0;
    overflow: hidden;
`;

const TransformBox = styled(a.div)`
    display: flex;
    height: 80vh;
    will-change: transform;
`;

const BlockVideosDesktop = () => {
    const { t } = useTranslation();
    return (
        <ScrollCarousel>
            <BlockVideoItem
                video_id="EfWg8-2jkaU"
                title={t("block_video_one_title")}
            />
            <BlockVideoItem
                video_id="L6_O_uk0GEs"
                title={t("block_video_two_title")}
            />
            <BlockVideoItem
                video_id="5NSYJIXpTp4"
                title={t("block_video_three_title")}
            />
            <BlockVideoItem
                video_id="MP0SKD1J_IE"
                title={t("block_video_four_title")}
            />
            <BlockVideoItem
                video_id="PNV7Z1zwy44"
                title={t("block_video_five_title")}
            />
        </ScrollCarousel>
    );
};
const BlockVideosMobile = () => {
    const { t } = useTranslation();
    return (
        <Container size="large">
            <STitle>
                <Headline level={2} size="huge" offset={["top-0", "bottom-0"]}>
                    {t("block_video_title")}
                </Headline>
            </STitle>
            <SList>
                <BlockVideoItem
                    video_id="EfWg8-2jkaU"
                    title={t("block_video_one_title")}
                />
                <BlockVideoItem
                    video_id="L6_O_uk0GEs"
                    title={t("block_video_two_title")}
                />
                <BlockVideoItem
                    video_id="5NSYJIXpTp4"
                    title={t("block_video_three_title")}
                />
                <BlockVideoItem
                    video_id="MP0SKD1J_IE"
                    title={t("block_video_four_title")}
                />
                <BlockVideoItem
                    video_id="PNV7Z1zwy44"
                    title={t("block_video_five_title")}
                />
            </SList>
        </Container>
    );
};
const BlockVideos = () => {
    const onlyWidth = useWindowWidth();
    return (
        <SBlockVideos>
            {isBrowser && onlyWidth > grid_down.md ? (
                <BlockVideosDesktop />
            ) : isBrowser ? (
                <BlockVideosMobile />
            ) : null}
        </SBlockVideos>
    );
};

const SBlockVideos = styled.div`
    padding: ${spacings?.offset_40} 0 ${spacings?.offset_80};
    ${down("md")} {
    }
`;

const SList = styled.div``;

export default BlockVideos;
